'use client'

import { Avatar, Icon, IconComponent } from '@graftcode/design-system'
import * as Dialog from '@radix-ui/react-dialog'

import { getSafeImageUrl } from '@common/core/helpers'
import type { Project } from '@common/modules/projects/types/project.types'

import { getInitials } from '../../../Sidebar/Sidebar.helpers'

import styles from '../../MobileMenu.module.scss'

interface ProjectPickerDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback to close the drawer */
  onClose: () => void
  /** List of projects */
  projects: Project[]
  /** Currently selected project ID */
  currentProjectId?: string
  onProjectSelect: (projectId: string) => void
  onOpenProjectSettings?: () => void
  onOpenProjectMembers?: () => void
  onInviteClick?: () => void
}

export function ProjectPickerDrawer({
  isOpen,
  onClose,
  projects,
  currentProjectId,
  onProjectSelect,
  onOpenProjectSettings,
  onOpenProjectMembers,
  onInviteClick,
}: ProjectPickerDrawerProps) {
  const handleProjectClick = (projectId: string) => {
    onProjectSelect(projectId)
    onClose()
  }

  const handleSettingsClick = () => {
    onOpenProjectSettings?.()
    onClose()
  }

  const handleMembersClick = () => {
    onOpenProjectMembers?.()
    onClose()
  }

  const handleInviteClick = () => {
    onInviteClick?.()
    onClose()
  }

  const settingsEnabled = Boolean(onOpenProjectSettings)
  const projectMembersEnabled = Boolean(onOpenProjectMembers)
  const inviteEnabled = Boolean(onInviteClick)

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.appPickerOverlay} />
        <Dialog.Content className={styles.appPickerDrawer}>
          {/* Header */}
          <div className={styles.appPickerHeader}>
            <div className={styles.appPickerDragHandle} />
            <Dialog.Title className={styles.appPickerTitle}>Select project</Dialog.Title>
            <div className={styles.appPickerDivider} />
          </div>

          {/* Content */}
          <div className={styles.appPickerContent}>
            <button
              type="button"
              className={`${styles.appPickerItem} ${!settingsEnabled ? styles.disabled : ''}`}
              disabled={!settingsEnabled}
              onClick={handleSettingsClick}
            >
              <IconComponent icon={Icon.SettingsRegular} size={24} className={styles.appPickerItemIcon} />
              <span className={styles.appPickerItemLabel}>Settings</span>
            </button>
            <button
              type="button"
              className={`${styles.appPickerItem} ${!projectMembersEnabled ? styles.disabled : ''}`}
              disabled={!projectMembersEnabled}
              onClick={handleMembersClick}
            >
              <IconComponent icon={Icon.PeopleRegular} size={24} className={styles.appPickerItemIcon} />
              <span className={styles.appPickerItemLabel}>Project members</span>
            </button>
            <button
              type="button"
              className={`${styles.appPickerItem} ${!inviteEnabled ? styles.disabled : ''}`}
              disabled={!inviteEnabled}
              onClick={handleInviteClick}
            >
              <IconComponent icon={Icon.PeopleAddRegular} size={24} className={styles.appPickerItemIcon} />
              <span className={styles.appPickerItemLabel}>Invite people</span>
            </button>

            {/* Separator */}
            <div className={styles.accountDrawerSeparator} />

            {/* Switch project label */}
            <div className={styles.pickerSectionLabel}>Switch project</div>

            {/* Project List */}
            {projects.length === 0 ? (
              <div className={styles.pickerEmptyMessage}>No projects available</div>
            ) : (
              projects.map((project) => {
                const isSelected = project.id === currentProjectId
                const initials = getInitials(project.name)

                return (
                  <button
                    key={project.id}
                    className={`${styles.appPickerItem} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleProjectClick(project.id)}
                  >
                    <Avatar size={24} src={getSafeImageUrl(project.logo)} fallback={initials} />
                    <span className={styles.appPickerItemLabel}>{project.name}</span>
                    {isSelected && (
                      <IconComponent icon={Icon.CheckmarkRegular} size={24} className={styles.appPickerCheckmark} />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
